/**
 * Resolves a Cloud Storage object path into a viewable download URL.
 *
 * @module useCandidDocumentDownloadUrl
 */
import { useEffect, useState } from "react";
import { getFileDownloadUrl } from "../../../../api/firebase/firestoreService";

/**
 * In-session cache of resolved download URLs, keyed by storage path.
 *
 * Lives at module scope: unmounting the preview (or closing the modal)
 * does not clear entries. Download URLs embed a long-lived token, so
 * reusing them lets a tab re-open instantly instead of re-issuing a
 * `getDownloadURL` round-trip. The map is dropped only on full page
 * reload (or a module hot-reload).
 */
const downloadUrlCache = new Map<string, string>();

/** Message shown when the object cannot be resolved. */
const DOWNLOAD_URL_ERROR = "Unable to load this document from storage.";

/** Resolution state for one document path. */
export interface CandidDocumentDownloadUrlState {
  /** Viewable URL, or an empty string while loading or on failure. */
  url: string;
  /** True while the download URL is being resolved. */
  isLoading: boolean;
  /** Human-readable failure message, or an empty string. */
  error: string;
}

/**
 * Returns true when the value is already a fetchable URL.
 *
 * @param value - Storage path or absolute URL
 * @returns Whether the value can be used without a Storage lookup
 */
const isHttpUrl = (value: string): boolean => /^https?:\/\//i.test(value);

/**
 * Strips leading slashes so `/medical_pa_order/...` resolves like
 * `medical_pa_order/...` against the configured bucket.
 *
 * @param gcsPath - Bucket-relative object path
 * @returns Path without leading slashes
 */
const normalizeStoragePath = (gcsPath: string): string =>
  gcsPath.replace(/^\/+/, "");

/**
 * Returns a URL already known for this path, without a network call.
 *
 * @param gcsPath - Bucket-relative object path or absolute URL
 * @returns Cached or pass-through URL, or an empty string
 */
const readKnownUrl = (gcsPath: string): string => {
  if (isHttpUrl(gcsPath)) {
    return gcsPath;
  }
  return downloadUrlCache.get(normalizeStoragePath(gcsPath)) ?? "";
};

/**
 * Resolves a Cloud Storage path (e.g.
 * `/medical_pa_order/<order_id>/j_code_docs/<uuid>.pdf`) into a download
 * URL, tracking loading and error state. Absolute URLs pass through.
 * Resolved URLs are written to {@link downloadUrlCache} for the session.
 * Effect cleanup only marks the in-flight request stale so `setState`
 * is not called after unmount; it does not omit cache entries.
 *
 * @param gcsPath - Bucket-relative object path, absolute URL, or empty
 * @returns Download URL plus loading and error flags
 */
export const useCandidDocumentDownloadUrl = (
  gcsPath: string,
): CandidDocumentDownloadUrlState => {
  const [state, setState] = useState<CandidDocumentDownloadUrlState>(() => ({
    url: readKnownUrl(gcsPath),
    isLoading: Boolean(gcsPath) && !readKnownUrl(gcsPath),
    error: "",
  }));

  useEffect(() => {
    if (!gcsPath) {
      setState({ url: "", isLoading: false, error: "" });
      return;
    }

    const knownUrl = readKnownUrl(gcsPath);
    if (knownUrl) {
      setState({ url: knownUrl, isLoading: false, error: "" });
      return;
    }

    let isStale = false;
    const storagePath = normalizeStoragePath(gcsPath);
    setState({ url: "", isLoading: true, error: "" });

    getFileDownloadUrl(storagePath)
      .then((downloadUrl) => {
        if (isStale) {
          return;
        }
        if (!downloadUrl) {
          setState({ url: "", isLoading: false, error: DOWNLOAD_URL_ERROR });
          return;
        }
        downloadUrlCache.set(storagePath, downloadUrl);
        setState({ url: downloadUrl, isLoading: false, error: "" });
      })
      .catch(() => {
        if (!isStale) {
          setState({ url: "", isLoading: false, error: DOWNLOAD_URL_ERROR });
        }
      });

    return () => {
      isStale = true;
    };
  }, [gcsPath]);

  return state;
};
